import { AfterContentInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Client } from 'src/app/domain/client';
import { EFrequency } from 'src/app/domain/enum/frequency';
import { EStrategyPortfolioDocumentType } from 'src/app/domain/enum/strategy-portfolio-document-type';
import { EStrategyStatus } from 'src/app/domain/enum/strategy-status';
import { ESubSectionType } from 'src/app/domain/enum/sub-section-type';
import { Partner } from 'src/app/domain/partner';
import { PortfolioDocument } from 'src/app/domain/portfolio-document';
import { SamplingPoint } from 'src/app/domain/sampling-point';
import { Strategy } from 'src/app/domain/strategy';
import { User } from 'src/app/domain/user';
import { ENavigationRoute } from 'src/app/model/enum/navigation-route';
import { Localisation } from 'src/app/model/localisation.model';
import { AddressService } from 'src/app/services/address.service';
import { ClientService } from 'src/app/services/client.service';
import { DocumentService } from 'src/app/services/document.service';
import { PDREDonneesAdministrativesDTO } from 'src/app/services/DTO/PDRE-donnees-administratives.dto';
import { StrategyService } from 'src/app/services/strategy.service';
import { UserService } from 'src/app/services/user.service';
import { CoreData } from 'src/app/tools/core-data/core-data';
import { AlternateContactBoxComponent } from 'src/app/tools/ui/alternate-contact-box/alternate-contact-box.component';
import { ConfirmBoxDialog } from 'src/app/tools/ui/confirm-box/confirm-box.component';
import { EditTextBoxDialog } from 'src/app/tools/ui/edit-text-box/edit-text-box';
import { EDocumentType } from 'src/app/tools/ui/mutilple-file-upload-box/document-type';
import { MultipleFileUploadBoxDialog } from 'src/app/tools/ui/mutilple-file-upload-box/multiple-file-upload-box';
import { NotificationManager } from 'src/app/tools/ui/notification-manager';
import { StringConverter } from 'src/app/tools/ui/string-converter';

@Component({
    selector: 'app-strategy',
    templateUrl: './strategy.component.html',
    styleUrls: ['./strategy.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StrategyComponent implements OnInit, AfterContentInit {
    EDocumentType = EDocumentType;
    public MAX_LENGTH = 50;
    public readonly PDRE_FILE_NAME = 'PDRE.pdf';

    public Model: Strategy = new Strategy();
    public PartnerList: Partner[] = [];

    ValidWorkDurationNumber = new UntypedFormControl('', Validators.compose([
        Validators.min(0),
        Validators.max(100)]));

    ValidFullAddress = new UntypedFormControl('', Validators.compose([
        Validators.required,
        this.checkAddressForm()]));

    ValidMaxLength = new UntypedFormControl('', [Validators.maxLength(this.MAX_LENGTH)]);

    ValidAddtionalAddressMaxLength = new UntypedFormControl('', [Validators.maxLength(this.MAX_LENGTH)]);

    public IsCreation: boolean = true;
    SubSectionTypeList: string[] = Object.values(ESubSectionType).filter(x => !isNaN(Number(x))).map(x => StringConverter.getSubSectionTypeName(x as ESubSectionType));
    FrequencyList: string[] = Object.values(EFrequency).filter(x => !isNaN(Number(x))).map(x => StringConverter.getFDXFrequencyName(x as EFrequency));

    ContactList: User[] = [];

    private subject: Subject<string> = new Subject();
    SearchedText: string;
    MatchingAddressList: Localisation[] = [];
    SelectedAddress: Localisation;

    // For internal user to search clients
    IsExternal: boolean = false;
    ClientSearchedText: string;
    ClientList: Client[] = [];
    private clientSubject: Subject<string> = new Subject();
    // End of internal user to search clients 

    get IsDateAndDurationMandatory() {
        if (!this.Model.IsFDX)
            return true;
        if (this.Model.Work.HasBeforeWork ||
            // this.Model.Work.HasPreliminaryWork ||
            this.Model.Work.HasDuringWorkOperator ||
            this.Model.Work.HasDuringWorkEnvironmental ||
            // this.Model.Work.HasPostWork ||
            this.Model.Work.HasEndOfAsbestosWork2 ||
            this.Model.Work.HasEndOfAsbestosWork)
            return true;
        return false;
    }

    constructor(
        private coreData: CoreData,
        private route: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
        private strategyService: StrategyService,
        private addressService: AddressService,
        private documentService: DocumentService,
        private userService: UserService,
        private clientService: ClientService,
    ) { }

    ngOnInit(): void {
    }

    ngAfterContentInit(): void {
        this.ValidWorkDurationNumber.markAsTouched();
        // this.ValidWorkDurationNumberWithRequired.markAsTouched();
        this.ValidFullAddress.markAsTouched();
        this.ValidMaxLength.markAsTouched();

        const id = this.route.snapshot.params['id'];

        this.userService.getMyUser().subscribe(x => {
            this.IsExternal = x?.IsExternal;
            this.ClientList = x?.ClientList;
        });

        if (this.ClientList.length === 1) {
            this.Model.Portfolio.Client = this.ClientList[0];
            this.updateClient();
        }
        this.Model.Work.WorkPeriodicity = EFrequency.Day;

        if (id) {
            this.IsCreation = false;
            this.strategyService.getStrategyForOrder(id).subscribe(x => {
                if (x) {
                    this.Model = x;

                    this.Model.Portfolio.Client = this.ClientList.find(x => x.Code === this.Model.Portfolio.ClientCode)!;
                    this.SearchedText = `${this.Model.Portfolio.SamplingPoint.Address} ${this.Model.Portfolio.SamplingPoint.ZipCode} ${this.Model.Portfolio.SamplingPoint.City}`;
                    if (this.Model.Status !== EStrategyStatus.Ordered) {
                        this.ValidWorkDurationNumber.disable();
                        // this.ValidWorkDurationNumberWithRequired.disable();
                        this.ValidFullAddress.disable();
                    }

                    this.getPartnerByZipcode(false);
                }
            });
        }
        this.subject.pipe(debounceTime(500)).subscribe(() =>
            this.searchAddressList()
        );
        this.clientSubject.pipe(debounceTime(500)).subscribe(() =>
            this.getClientList()
        );
    }

    hasPDREDocument() {
        return this.Model.Portfolio.PortfolioDocuments.some(d => d.FileName === this.PDRE_FILE_NAME);
    }

    onKeyUpForClient() {
        if (!this.ClientSearchedText || this.ClientSearchedText.length < 3) {
            this.ClientList = [];
            return;
        }

        this.clientSubject.next('');
    }

    reassignForm() {
        if (!this.Model.IsFDX) {
            this.ValidWorkDurationNumber = new UntypedFormControl('', Validators.compose([
                Validators.required,
                Validators.min(0),
                Validators.max(100)]));
            return;
        }

        this.ValidWorkDurationNumber = new UntypedFormControl('', Validators.compose([
            Validators.min(0),
            Validators.max(100)]));
    }

    openElementNecessaire() {
        window.open(`${window.location.origin}/assets/Elements nécessaires à l'élaboration d'une stratégie.pdf`, '_blank');
    }

    verifyAddress(text: string, samplingPoint: SamplingPoint) {

        return text?.includes(samplingPoint?.City) &&
            text?.includes(samplingPoint?.Address);
    }

    checkAddressForm(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            // Do not test initialisation value
            return control.value === '' || !(typeof control.value === "string") ||
                this.ValidFullAddress.value === undefined ||
                (this.Model && this.verifyAddress(control.value, this.Model.Portfolio.SamplingPoint.City ? this.Model.Portfolio.SamplingPoint : null))

                ? null : { wrongColor: control.value };
        }
    }

    getPartnerByZipcode(withPreselection: boolean) {
        this.addressService.getPartners(this.Model.Portfolio.SamplingPoint.ZipCode).subscribe(x => {
            this.PartnerList = x;
            if (this.PartnerList.length === 1 && withPreselection)
                this.Model.Portfolio.PartnerCode = this.PartnerList[0].PartnerCode;
        });
    }

    IsEditable() {
        // return this.Model.Status === EStrategyStatus.Ordered;
        return true;
    }

    onKeyUp() {
        if (!this.SearchedText || this.SearchedText.length < 3) {
            this.MatchingAddressList = [];
            return;
        }

        this.subject.next();
    }

    assignSelectedClient(event: MatAutocompleteSelectedEvent) {
        this.Model.Portfolio.Client = this.ClientList.find(x => x.Code === event.option.value.Code)!;
        this.updateClient();
    }

    assignSelectedAddress(event: MatAutocompleteSelectedEvent) {
        this.SelectedAddress = this.MatchingAddressList.find(x => this.verifyAddress(event.option.value, { City: x.City, Address: x.Name, ZipCode: x.PostCode } as any));
        this.Model.Portfolio.SamplingPoint.City = this.SelectedAddress.City;
        this.Model.Portfolio.SamplingPoint.ZipCode = this.SelectedAddress.PostCode;
        this.Model.Portfolio.SamplingPoint.Address = this.SelectedAddress.Name;

        this.ValidFullAddress.updateValueAndValidity();
        this.getPartnerByZipcode(true);
    }

    searchAddressList() {
        this.MatchingAddressList = [];
        this.addressService.getMatchingAddressList(this.SearchedText).subscribe(x => {
            this.MatchingAddressList = x;
        });
    }

    updateClient() {
        // Mapping because of Domain shitness
        this.Model.Portfolio.ClientCode = this.Model.Portfolio.Client.Code;
        this.Model.Portfolio.ClientName = this.Model.Portfolio.Client.Name;
        this.Model.Portfolio.AlternateContactList = [];
        this.ClientSearchedText = this.Model.Portfolio.Client.Code + ' - ' + this.Model.Portfolio.Client.Name + ' ' + this.Model.Portfolio.Client.City;
    }

    canSave() {
        if (this.Model.IsFDX)
            return (this.IsDateAndDurationMandatory && this.Model.Work.StartDate && (this.IsCreation ? this.ValidWorkDurationNumber.valid : true) || !this.IsDateAndDurationMandatory) &&
                this.Model.Portfolio.Client &&
                this.Model.Portfolio?.SamplingPoint?.Name &&
                this.verifyAddress(this.SearchedText, this.Model.Portfolio.SamplingPoint);
        return this.Model.Work.StartDate &&
            this.Model.Portfolio.Client &&
            this.Model.Portfolio?.SamplingPoint?.Name &&
            (this.IsCreation ? this.ValidWorkDurationNumber.valid : true) &&
            this.verifyAddress(this.SearchedText, this.Model.Portfolio.SamplingPoint);
    }

    openDocumentDialog(type?: EDocumentType) {
        const dialogRef = this.dialog.open(MultipleFileUploadBoxDialog, {
            data: { type },
            width: '777px',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) { // result = file name
                result.forEach((data: string | PDREDonneesAdministrativesDTO) => {

                    let d = new PortfolioDocument();
                    if (type === EDocumentType.PDRE && data instanceof PDREDonneesAdministrativesDTO) {

                        d.FileName = this.PDRE_FILE_NAME;
                        d.Types = [EStrategyPortfolioDocumentType.PDREDematamiante];
                        if (!data.ExtractionSuccessful) {
                            NotificationManager.handleWarning('Le fichier envoyé ne correspond pas au format Demat@miante. Aucun champ prérempli.', this.snackBar);
                            d.Types = [EStrategyPortfolioDocumentType.PDRECustom];
                            this.Model.Portfolio.PortfolioDocuments.push(d);
                            return;
                        }

                        // this.Model.Portfolio.ClientName = data.NomClient;
                        // this.Model.Portfolio. = name.Siret;
                        this.Model.Portfolio.SamplingPoint.Address = data.AdresseChantier;
                        this.Model.Portfolio.SamplingPoint.ZipCode = data.CodePostal;
                        this.Model.Portfolio.SamplingPoint.City = data.Ville;
                        this.Model.Work.Type = data.TypeOperation;
                        this.Model.Work.StartDate = data.DebutChantier;
                        this.Model.Work.WorkDuration = data.DureeChantier;
                        this.Model.Work.WorkPeriodicity = data.UniteDuree;
                        this.Model.Work.NumberOfOperators = data.NbOperateurs;
                        this.Model.Portfolio.SamplingPoint.Name = data.NomChantier;
                        this.Model.Portfolio.EDICode = data.ReferenceInterne;

                        if (data.Client) {
                            if (!this.ClientList.find(c => c.Code === data.Client.Code))
                                this.ClientList.push(data.Client);
                            this.Model.Portfolio.Client = data.Client;
                            this.updateClient();
                        }

                        this.getPartnerByZipcode(true);

                        this.Model.Portfolio.AlternateContactList = data.ContactList.map(c => {
                            let contact = new User();
                            contact.FirstName = c.Prenom;
                            contact.LastName = c.Nom;
                            contact.Email = c.Courriel;
                            // contact.Role = c.Role;
                            return contact;
                        });
                        this.SearchedText = `${this.Model.Portfolio.SamplingPoint.Address} ${this.Model.Portfolio.SamplingPoint.ZipCode} ${this.Model.Portfolio.SamplingPoint.City}`;
                    } else {
                        d.FileName = data as string;
                    }

                    this.Model.Portfolio.PortfolioDocuments.push(d);
                });
            }
        });
    }

    openContactDialog() {
        if (!this.Model.Portfolio.Client) {
            NotificationManager.handleWarning('Veuillez sélectionner un client avant d\'ajouter un contact.', this.snackBar);
            return;
        }

        const dialogRef = this.dialog.open(AlternateContactBoxComponent, {
            data: {
                clientCode: this.Model.Portfolio.Client.Code,
                alreadyAddedContactList: this.Model.Portfolio.AlternateContactList
            },
            width: '555px',
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.Model.Portfolio.AlternateContactList.push(result);
                NotificationManager.handleSuccess('Contact ajouté !', this.snackBar);
            }
        });
    }

    deleteDocument(d: PortfolioDocument) {

        _.remove(this.Model.Portfolio.PortfolioDocuments, x => x.FileName === d.FileName);
        NotificationManager.handleSuccess('Document supprimé', this.snackBar);
    }

    deleteContact(contact: User) {
        _.remove(this.Model.Portfolio.AlternateContactList, x => x.Email === contact.Email);
    }

    navigateToStrategyList() {
        this.router.navigate([ENavigationRoute.Home]);
    }

    getClientList() {
        this.ClientList = [];
        this.clientService.getClientList(this.ClientSearchedText).subscribe(x => {
            this.ClientList = x;
        });
    }

    save() {

        if (this.IsCreation) {
            this.strategyService.createStrategyDemand(this.Model).subscribe(x => {
                if (x) {
                    if (this.Model.ContexteHorsStrategie) {
                        // NotificationManager.handleSuccess('Stratégie créée !', this.snackBar);
                        this.router.navigate([ENavigationRoute.Order, { id: x }]);
                    } else {
                        NotificationManager.handleSuccess('Stratégie créée !', this.snackBar);
                        this.router.navigate([ENavigationRoute.Home]);
                    }
                }
            });
        } else {
            this.strategyService.updateStrategyDemand(this.Model).subscribe(x => {
                if (x) {
                    if (this.Model.ContexteHorsStrategie) {
                        this.router.navigate([ENavigationRoute.Order, { id: x }]);
                    } else {
                        NotificationManager.handleSuccess('Stratégie modifiée !', this.snackBar);
                        this.router.navigate([ENavigationRoute.Home]);
                    }
                }
            });
        }
    }

    deleteStrategy() {
        const dialogRef = this.dialog.open(ConfirmBoxDialog, { width: '666px', data: { message: StringConverter.getConfirmMessageByLabel('confirm-removal-strategy'), withCancelButton: true } });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.strategyService.deleteStrategyDemand(this.Model).subscribe(x => {
                    if (x) {
                        NotificationManager.handleSuccess('Stratégie suprimée !', this.snackBar);
                        this.router.navigate([ENavigationRoute.Home]);
                    }
                });
            }
        });
    }

    openEditTextBox() {
        const dialogRef = this.dialog.open(EditTextBoxDialog, {
            data: {
                title: 'Processus',
                text: ''
            },
            width: '777px',
            disableClose: true
        });

        dialogRef.afterClosed().subscribe((result: string) => {
            if (result) {
                this.Model.ProcessList.push(result)
            }
        });
    }

    deleteProcess(process: string) {
        _.remove(this.Model.ProcessList, x => x === process)
    }

    compareWith(m1: Client, m2: Client) {
        return m1 && m2 ? m1.Code === m2.Code : m1 === m2;
    }

    // comparePartnerWith(m1: Partner, m2: Partner) {
    //   return m1 && m2 ? m1.PartnerCode === m2.PartnerCode : m1 === m2;
    // }

    compareLocalisationWith(m1: Localisation, m2: Localisation) {
        return m1 && m2 ? m1.Id === m2.Id : m1 === m2;
    }

    displayFn(loc: Localisation | Partner | Client | string) {
        if (!loc) {
            return '';
        }
        if (loc instanceof Client) {
            return loc ? `${loc.Code} - ${loc.Name}` : '';
        }
        if (loc instanceof Localisation) {
            return loc ? loc.Label : '';
        }
        if (loc instanceof Partner) {
            return loc ? `${loc.PartnerName} / ${loc.PartnerCode}` : '';
        }
        if (typeof (loc) === "string") {
            return loc;
        }
    }
}
